import { useQuery } from "@tanstack/react-query";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import {
  moetNaarOnboarding,
  ONBOARDING_PAD,
  type OnboardingToestand,
} from "@/domain/onboarding/onboardingGate";
import { fetchOnboardingStatus } from "@/services/legalAcceptanceService";
import { useAuth } from "@/hooks/useAuth";
import { useGovernanceActorProfile } from "@/hooks/useGovernanceActorProfile";
import supabase from "@/lib/supabase";
import { magCollectrVoorbereiden } from "@/domain/onboarding/onboardingGate";
import { Button } from "@/components/ui/button";

/**
 * WP-3 — houdt een tenant die de onboarding nog niet heeft afgerond bij de rest
 * van de applicatie weg. De beslissing zelf staat in `moetNaarOnboarding`, zodat
 * ze toetsbaar is zonder router.
 *
 * Dit component staat bewust in een eigen module en niet in `App.tsx`: dat
 * bestand bouwt de router op moduleniveau op, waardoor een test die het
 * importeert de volledige applicatie met honderd lazy pagina's meetrekt.
 *
 * Bij onbekende status toont de gate opnieuw controleren en uitloggen.
 * Nieuwe tenants hebben daarnaast een databasegrendel en serverpoort.
 */
export function OnboardingGate() {
  const location = useLocation();
  const { session, user, loading: authLoading } = useAuth();
  const actorId = session?.user?.id ?? user?.id ?? null;
  const { profile, isLoading: profielLaadt } = useGovernanceActorProfile(actorId);

  /*
   * DE SLEUTEL DRAAGT DE CONTEXT. Het oordeel hangt af van rol én gekozen tenant, dus
   * die horen in de query-sleutel: wie van platformcontext naar een tenant schakelt,
   * krijgt automatisch een nieuw oordeel in plaats van het gecachete vorige. Een
   * invalidatielijst elders zou dit ook kunnen, maar die moet iemand bijhouden — deze
   * sleutel niet.
   */
  const actor = profile
    ? {
      role: profile.role,
      tenantId: profile.tenant_id ?? null,
      platformEffectiveTenantId: profile.platform_effective_tenant_id ?? null,
    }
    : null;

  const statusQuery = useQuery({
    queryKey: [
      "onboarding-status",
      actor?.role ?? null,
      actor?.platformEffectiveTenantId ?? null,
      actor?.tenantId ?? null,
    ],
    queryFn: () => fetchOnboardingStatus(actor),
    enabled: Boolean(actorId) && Boolean(profile) && !authLoading && !profielLaadt,
    staleTime: 60_000,
  });

  if (authLoading || !actorId || profielLaadt || statusQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        Laden...
      </div>
    );
  }

  const toestand: OnboardingToestand = statusQuery.data ?? { soort: "onbekend" };
  if (toestand.soort === "onbekend") {
    return <div className="flex min-h-screen items-center justify-center bg-muted/30 p-6">
      <div role="alert" className="w-full max-w-lg space-y-5 rounded-xl border bg-background p-8 shadow-sm">
        <h1 className="text-xl font-semibold">Uw toegang controleren</h1>
        <p className="text-sm text-muted-foreground">De voorwaardenstatus kon niet worden gecontroleerd.</p>
        <div className="flex flex-wrap gap-3">
          <Button type="button" onClick={() => void statusQuery.refetch()} disabled={statusQuery.isFetching}>Opnieuw controleren</Button>
          <Button type="button" variant="outline" onClick={() => void supabase.auth.signOut()}>Uitloggen</Button>
        </div>
      </div>
    </div>;
  }
  const voorbereiding = actor?.role === "super_admin";
  if(toestand.soort==="legal-open"&&!(voorbereiding&&magCollectrVoorbereiden(location.pathname))){
    return <div className="space-y-4 p-6"><p>De juridische aanvraag is nog niet afgerond. Gebruik de afzonderlijke uitnodiging voor Legal docs. Collectr moet ook de ondertekende bestelbon goedkeuren.</p>
      <Button variant="outline" onClick={()=>void statusQuery.refetch()}>Status opnieuw controleren</Button>
      <Button variant="outline" onClick={()=>void supabase.auth.signOut()}>Uitloggen</Button></div>;
  }
  if (toestand.soort === "bekend" && toestand.voltooidOp === null) {
    if (voorbereiding && !magCollectrVoorbereiden(location.pathname)) {
      return <div className="space-y-3 p-6"><p>De klantbeheerder moet de voorwaarden nog aanvaarden. U kunt de inrichting voorbereiden.</p>
        <a href="/app/activatiecentrum" className="underline">Naar voorbereiding</a></div>;
    }
    if (!voorbereiding && actor?.role !== "tenant_admin") {
      return <div className="space-y-3 p-6"><p>Uw klantbeheerder moet de voorwaarden en verwerkersovereenkomst nog aanvaarden.</p>
        <Button type="button" onClick={() => void supabase.auth.signOut()} variant="outline" size="sm">Uitloggen</Button></div>;
    }
  }
  if (moetNaarOnboarding(toestand, location.pathname, voorbereiding)) {
    return <Navigate to={ONBOARDING_PAD} replace />;
  }

  return <Outlet />;
}
